/**
 * Route Preloading Utilities
 *
 * Preload route components and data on hover/interaction for instant page transitions.
 * Component preloading fetches the JS bundle; data prefetching primes the TanStack Query
 * cache so the page renders with data immediately on navigation.
 */

import { queryClient } from '@/app/providers'
import { queryPolicies } from '@/shared/api/query-policies'

// Cache of preloaded routes to avoid duplicate fetches
const preloadedRoutes = new Set<string>()
const prefetchedRoutes = new Set<string>()

/**
 * Preload a route's lazy component
 * @param routePath - The route path to preload (e.g., '/agents', '/missions/designer')
 */
export const preloadRoute = (routePath: string): void => {
  if (preloadedRoutes.has(routePath)) {
    return // Already preloaded
  }

  preloadedRoutes.add(routePath)

  // Map route paths to their lazy component imports
  const routeMap: Record<string, () => Promise<any>> = {
    '/': () => import('@/pages/MissionCommand'),
    '/agents': () => import('@/pages/AgentsPage'),
    '/missions': () => import('@/pages/MissionCommand'),
    '/missions/designer': () => import('@/pages/MissionDesignerV4Page'),
    '/analytics': () => import('@/pages/AnalyticsPage'),
    '/governance': () => import('@/pages/GovernancePage'),
    '/governance/designer': () => import('@/pages/governance/GovernanceDesigner'),
    '/governance/trust': () => import('@/pages/governance/TrustPage'),
    '/governance/resources': () => import('@/pages/governance/ResourceManagementPageV2'),
    '/users': () => import('@/pages/UsersPage'),
    '/settings': () => import('@/pages/SettingsPage'),
    '/profile': () => import('@/pages/ProfilePage'),
  }

  const loader = routeMap[routePath]
  if (loader) {
    loader().catch(err => {
      console.warn(`Failed to preload route ${routePath}:`, err)
      preloadedRoutes.delete(routePath) // Allow retry
    })
  }
}

/**
 * Prefetch route data into the TanStack Query cache.
 * Uses dynamic imports so service modules are only loaded when needed.
 */
export const prefetchRouteData = (routePath: string): void => {
  if (prefetchedRoutes.has(routePath)) return
  prefetchedRoutes.add(routePath)

  const prefetchMap: Record<string, () => Promise<void>> = {
    '/': () => prefetchMissions(),
    '/missions': () => prefetchMissions(),
    '/agents': async () => {
      const [{ agentService }, { agentKeys }] = await Promise.all([
        import('@/shared/api/agent.service'),
        import('@/hooks/useAgentData'),
      ])
      queryClient.prefetchQuery({
        queryKey: agentKeys.list(1, 500),
        queryFn: () => agentService.listAgents({ limit: 500, page: 1 }),
        ...queryPolicies.pagedCatalog,
      })
    },
    '/settings': async () => {
      const [{ budgetMonitoringQueryOptions, settingsKeys }, { settingsService }] = await Promise.all([
        import('@/hooks/useSettingsData'),
        import('@/services/settings.service'),
      ])
      queryClient.prefetchQuery({
        queryKey: settingsKeys.costNotifications,
        queryFn: () => settingsService.getCostNotificationSettings(),
        ...queryPolicies.catalog,
      })
      queryClient.prefetchQuery({
        ...budgetMonitoringQueryOptions,
      })
    },
  }

  const prefetcher = prefetchMap[routePath]
  if (prefetcher) {
    prefetcher().catch(err => {
      console.warn(`Failed to prefetch data for ${routePath}:`, err)
      prefetchedRoutes.delete(routePath) // Allow retry
    })
  }
}

async function prefetchMissions() {
  const [{ missionAdvancedService }, { missionKeys }] = await Promise.all([
    import('@/shared/api/mission-advanced.service'),
    import('@/hooks/useMissionData'),
  ])
  const defaultParams = { status: 'all' as const, limit: 25, offset: 0 }
  queryClient.prefetchQuery({
    queryKey: missionKeys.list(defaultParams),
    queryFn: () => missionAdvancedService.getMissions({ limit: 25, offset: 0 }),
    ...queryPolicies.pagedCatalog,
  })
  queryClient.prefetchQuery({
    queryKey: missionKeys.statistics,
    queryFn: () => missionAdvancedService.getStatistics(),
    ...queryPolicies.operational,
  })
}

/**
 * Setup preload listeners on navigation links
 * Call this in your main layout component
 */
export const setupRoutePreloading = (): void => {
  // Preload on link hover
  document.addEventListener('mouseover', (e) => {
    const target = e.target as HTMLElement
    const link = target.closest('a[href^="/"]') as HTMLAnchorElement
    if (link && link.href) {
      const url = new URL(link.href)
      preloadRoute(url.pathname)
      prefetchRouteData(url.pathname)
    }
  }, { passive: true })

  // Preload on link focus (keyboard navigation)
  document.addEventListener('focusin', (e) => {
    const target = e.target as HTMLElement
    if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('/')) {
      const href = target.getAttribute('href')!
      preloadRoute(href)
      prefetchRouteData(href)
    }
  }, { passive: true })
}

/**
 * Preload critical routes on app load
 * Call this after initial render to preload commonly accessed pages
 */
export const preloadCriticalRoutes = (): void => {
  // Wait for initial render to complete
  requestIdleCallback(() => {
    // Preload + prefetch missions (landing page)
    preloadRoute('/missions')
    prefetchRouteData('/missions')
    // Preload + prefetch agents page (commonly accessed)
    preloadRoute('/agents')
    prefetchRouteData('/agents')
  }, { timeout: 2000 })
}

/**
 * Hook to use in React components for preloading
 */
export const useRoutePreloader = () => {
  return {
    preloadRoute,
    prefetchRouteData,
    preloadCriticalRoutes,
  }
}
